import { useState, useRef, useCallback, useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import Highlight from '@tiptap/extension-highlight'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import FormControl from '../FormControl'
import Menu from './components/Menu'
import { MathCalculator } from './extensions/MathCalculator'

interface MarkdownEditorProps {
  name: string
  allowHorizontalResize?: boolean
  label?: string
  placeholder?: string
  disabled?: boolean
  'data-cy'?: string
  formulaResultDecimalPlaces?: number
}

const StyledEditorWrapper = styled('div')<{ height: number; width: number }>(
  ({ theme, height, width }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.5),
    transition: 'border-color 0.2s ease-in-out',
    position: 'relative',
    width: width === 0 ? '100%' : `${width}px`,
    minWidth: '300px',
    '&:focus-within': {
      border: `1px solid ${theme.palette.primary.main}`,
      outline: 'none',
    },
    '& .ProseMirror': {
      height: `${height}px`,
      minHeight: '120px',
      paddingLeft: '10px',
      paddingRight: '10px',
      outline: 'none',
      overflow: 'auto',
    },
    '& .ProseMirror p.is-editor-empty:first-child::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      color: theme.palette.text.disabled,
      pointerEvents: 'none',
      height: 0,
    },
    '& .ProseMirror .is-editor-empty:first-child::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      color: theme.palette.text.disabled,
      pointerEvents: 'none',
      height: 0,
    },
  }),
)

const DragHandle = styled(Box)<{ allowHorizontalResize: boolean }>(
  ({ theme, allowHorizontalResize }) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '16px',
    height: '16px',
    cursor: allowHorizontalResize ? 'nw-resize' : 'ns-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease-in-out',
    borderRadius: '0 0 4px 0',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:active': {
      backgroundColor: theme.palette.action.selected,
    },
  }),
)

const MarkdownEditor = ({
  name,
  allowHorizontalResize = true,
  label,
  placeholder = 'Text eingeben...',
  disabled = false,
  'data-cy': dataCy,
  formulaResultDecimalPlaces = 2,
}: MarkdownEditorProps) => {
  const [height, setHeight] = useState(200)
  const [width, setWidth] = useState(0) // 0 means 100% width
  const [isDragging, setIsDragging] = useState(false)
  const dragStartY = useRef<number>(0)
  const dragStartX = useRef<number>(0)
  const dragStartHeight = useRef<number>(0)
  const dragStartWidth = useRef<number>(0)

  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({ name, control })

  // Create debounced version of the update handler
  const debouncedUpdate = useDebouncedCallback((editor: Editor) => {
    const html = editor.getHTML()
    onChange(html)
  }, 200)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Subscript,
      Superscript,
      MathCalculator.configure({
        resultDecimalPlaces: formulaResultDecimalPlaces,
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
      }),
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: value || '<p></p>',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      debouncedUpdate(editor)
    },
    onBlur: () => {
      // Ensure immediate update on blur
      debouncedUpdate.flush()
      onBlur()
    },
  })

  // Update editor content when form value changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '<p></p>')
    }
  }, [editor, value])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation() // Prevent DraggablePopup from interfering
      setIsDragging(true)
      dragStartY.current = e.clientY
      dragStartX.current = e.clientX
      dragStartHeight.current = height

      // If width is 0 (100%), get the actual computed width
      const currentWidth = width === 0 ? e.currentTarget.parentElement?.offsetWidth || 400 : width
      dragStartWidth.current = currentWidth
    },
    [height, width],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return

      const deltaY = e.clientY - dragStartY.current
      const newHeight = Math.max(120, dragStartHeight.current + deltaY)
      setHeight(newHeight)

      if (allowHorizontalResize) {
        const deltaX = e.clientX - dragStartX.current
        const newWidth = Math.max(300, dragStartWidth.current + deltaX)
        setWidth(newWidth)
      }
    },
    [isDragging, allowHorizontalResize],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDragging) {
        setIsDragging(false)
      }
    },
    [isDragging],
  )

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      // Use window instead of document for better event capture
      // Add capture: true to ensure we get events even when they're stopped by other elements
      window.addEventListener('mousemove', handleMouseMove, { capture: true, passive: true })
      window.addEventListener('mouseup', handleMouseUp, { capture: true })
      window.addEventListener('keydown', handleKeyDown, { capture: true })
      document.body.style.cursor = allowHorizontalResize ? 'nw-resize' : 'ns-resize'
      document.body.style.userSelect = 'none'
      document.body.style.pointerEvents = 'none' // Prevent other elements from interfering
    } else {
      window.removeEventListener('mousemove', handleMouseMove, { capture: true })
      window.removeEventListener('mouseup', handleMouseUp, { capture: true })
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove, { capture: true })
      window.removeEventListener('mouseup', handleMouseUp, { capture: true })
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleKeyDown, allowHorizontalResize])

  return (
    <FormControl data-cy={dataCy} label={label} error={error?.message}>
      <StyledEditorWrapper height={height} width={width} ref={ref}>
        <EditorContent editor={editor} />
        <Menu editor={editor} />
        <DragHandle
          allowHorizontalResize={allowHorizontalResize}
          onMouseDown={handleMouseDown}
          className="popover-content" // Prevent DraggablePopup from interfering
        >
          <DragIndicatorIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
        </DragHandle>
      </StyledEditorWrapper>
    </FormControl>
  )
}

export default MarkdownEditor
